import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { getRoleFallbackPath, isCsAllowedPath, isCsRole, isPjAllowedPath, isPjRole, isIndicadorAllowedPath, isIndicadorRole, canAccessRoute } from '@/config/routeAccess'
import type { Role } from '@/types'

interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRole?: Role | Role[]
}

export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { isAuthenticated, user, token, sessionUsesCookie, authReady } = useAuthStore()
  const location = useLocation()

  if (!authReady) {
    return (
      <div
        className='flex min-h-screen items-center justify-center bg-bg-primary'
        role='status'
        aria-label='Carregando sessão'
      >
        <div className='h-8 w-8 animate-spin rounded-full border-2 border-accent border-t-transparent' />
      </div>
    )
  }

  const hasValidSession = Boolean(token) || sessionUsesCookie

  if (!isAuthenticated || !user || !hasValidSession) {
    if (location.pathname.startsWith('/portal-indicacoes') || location.pathname.startsWith('/parceiros')) {
      return <Navigate to='/parceiros' state={{ from: location }} replace />
    }
    return <Navigate to='/login' state={{ from: location }} replace />
  }

  if (isIndicadorRole(user.role) && !isIndicadorAllowedPath(location.pathname)) {
    return <Navigate to='/parceiros/home' replace />
  }

  if (isPjRole(user.role) && !isPjAllowedPath(location.pathname)) {
    return <Navigate to='/portal' replace />
  }

  if (isCsRole(user.role) && !isCsAllowedPath(location.pathname)) {
    return <Navigate to='/cs/carteira' replace />
  }

  if (!canAccessRoute(user.role, location.pathname, user)) {
    const fallback = getRoleFallbackPath(user.role, location.pathname, user)
    if (fallback === location.pathname) {
      return (
        <div className='flex min-h-screen items-center justify-center bg-bg-primary p-6 text-center text-text-muted'>
          Sem permissão para esta página. Peça acesso ao administrador.
        </div>
      )
    }
    return <Navigate to={fallback} replace />
  }

  if (requiredRole) {
    const roles = (Array.isArray(requiredRole) ? requiredRole : [requiredRole]).map((r) =>
      r.toLowerCase(),
    )
    if (!roles.includes(user.role.toLowerCase())) {
      const fallback = getRoleFallbackPath(user.role, location.pathname, user)
      if (fallback === location.pathname) {
        return (
          <div className='flex min-h-screen items-center justify-center bg-bg-primary p-6 text-center text-text-muted'>
            Sem permissão para esta página. Peça acesso ao administrador.
          </div>
        )
      }
      return <Navigate to={fallback} replace />
    }
  }

  return <>{children}</>
}
